/** The assistant's messages in the app (top bar): what it found, newest first; a dot while some are unread. */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, BookOpenCheck, FileWarning, GitBranch, Library, Sparkles } from "lucide-react";
import { formatTimestamp } from "@brainlog/shared";
import { get, post } from "@/lib/api";
import { useMe } from "@/lib/queries";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { openLink, type AssistantLink } from "./openLink";

interface Event {
  id: string;
  kind: string;
  title: string;
  body: string;
  link: AssistantLink | null;
  createdAt: string;
  read: boolean;
}

const ICON: Record<string, React.ComponentType<{ className?: string }>> = { ai: Sparkles, papers: Library, readings: BookOpenCheck, references: FileWarning, repos: GitBranch };

export function NotificationsBell() {
  const qc = useQueryClient();
  const tz = useMe().data?.timezone;
  const [open, setOpen] = useState(false);
  const q = useQuery({ queryKey: ["assistant-inbox"], queryFn: () => get<{ events: Event[]; unread: boolean }>("/api/me/assistant/inbox"), refetchInterval: 20_000 });
  const read = async (ids: string[] | "all") => {
    await post("/api/me/assistant/inbox/read", { ids });
    await qc.invalidateQueries({ queryKey: ["assistant-inbox"] });
  };
  const events = q.data?.events ?? [];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={q.data?.unread ? "Notifications (new)" : "Notifications"} className="relative">
          <Bell />
          {q.data?.unread && <span className="absolute top-1 right-1 size-2 rounded-full bg-primary" aria-hidden />}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(24rem,calc(100vw-1rem))] p-0">
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <p className="flex-1 text-sm font-semibold">Notifications</p>
          {q.data?.unread && (
            <Button variant="ghost" size="xs" onClick={() => void read("all")}>
              Mark all as read
            </Button>
          )}
        </div>
        <ul className="max-h-96 divide-y overflow-y-auto" aria-label="Notifications">
          {events.map((e) => {
            const Icon = ICON[e.kind] ?? Bell;
            return (
              <li key={e.id}>
                <button
                  type="button"
                  className={cn("flex w-full gap-2.5 px-3 py-2.5 text-left text-sm hover:bg-accent/60", !e.read && "bg-primary/5")}
                  onClick={() => {
                    setOpen(false);
                    openLink(e.link);
                    if (!e.read) void read([e.id]);
                  }}
                >
                  <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className={cn("block", !e.read && "font-medium")}>{e.title}</span>
                    {e.body && <span className="line-clamp-2 block text-xs text-muted-foreground">{e.body}</span>}
                    <span className="block text-[0.7rem] text-muted-foreground">{formatTimestamp(e.createdAt, tz)}</span>
                  </span>
                </button>
              </li>
            );
          })}
          {!events.length && <li className="px-3 py-6 text-center text-sm text-muted-foreground">Nothing yet. Finished AI reviews and long AI work appear here, and so do new papers, readings, reference and repository checks once routines are on in a project's settings.</li>}
        </ul>
        <div className="border-t px-3 py-2">
          <button
            type="button"
            className="text-xs text-primary underline underline-offset-2"
            onClick={() => {
              setOpen(false);
              openLink({ type: "assistant" });
            }}
          >
            Choose what you receive, by email or Telegram
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
